import { useEffect, useMemo, useState } from 'react';
import { Fuel } from 'lucide-react';
import { operationsApi, type AssetRegistry, type FuelLog } from '../api';
import { FuelLogDetailModal } from '../components/FuelLogDetailModal';
import { PageHeader } from '@/lib/ui';

type Filter = 'all' | 'anomaly' | 'unverified';

export function FuelPage() {
  const [logs, setLogs] = useState<FuelLog[]>([]);
  const [assets, setAssets] = useState<Record<string, AssetRegistry>>({});
  const [filter, setFilter] = useState<Filter>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<FuelLog | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await operationsApi.listFuelLogs({
        anomaly_only: filter === 'anomaly',
        unverified_only: filter === 'unverified',
        limit: 200,
      });
      setLogs(rows);
    } catch (e) {
      setError((e as Error).message || 'Failed to load fuel logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // Resolve asset codes once for display.
  useEffect(() => {
    void operationsApi
      .listAssets({ limit: 500 })
      .then((res) => {
        const map: Record<string, AssetRegistry> = {};
        res.items.forEach((a) => (map[a.id] = a));
        setAssets(map);
      })
      .catch(() => undefined);
  }, []);

  const assetLabel = (id: string) => {
    const a = assets[id];
    return a ? `${a.code} · ${a.name}` : id;
  };

  const counts = useMemo(
    () => ({
      anomaly: logs.filter((l) => l.is_anomaly).length,
      unverified: logs.filter((l) => l.verified_at == null).length,
    }),
    [logs],
  );

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border px-6 py-5">
        <PageHeader
          title="Fuel logs"
          icon={Fuel}
          subtitle="Tenant-wide fuel feed. Click a row to view the receipt photo and verify the log."
          className="!mb-0"
        />
      </div>

      <div className="flex items-center gap-3 border-b border-border px-6 py-3">
        <div className="flex overflow-hidden rounded border border-border">
          <FilterBtn active={filter === 'all'} onClick={() => setFilter('all')}>
            All
          </FilterBtn>
          <FilterBtn active={filter === 'anomaly'} onClick={() => setFilter('anomaly')}>
            Anomalies
          </FilterBtn>
          <FilterBtn active={filter === 'unverified'} onClick={() => setFilter('unverified')}>
            Unverified
          </FilterBtn>
        </div>
        <span className="text-xs text-muted-foreground">
          {loading ? 'Loading…' : `${logs.length} shown`}
          {!loading && ` · ${counts.anomaly} anomaly · ${counts.unverified} unverified`}
        </span>
      </div>

      {error && (
        <div className="border-b border-rose-500/40 bg-rose-500/10 px-6 py-2 text-sm text-rose-300">
          {error}
        </div>
      )}

      <div className="flex-1 overflow-auto p-6">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-muted-foreground">
            <tr className="border-b border-border">
              <th className="py-2 pr-4">Logged</th>
              <th className="py-2 pr-4">Asset</th>
              <th className="py-2 pr-4">Liters</th>
              <th className="py-2 pr-4">Cost</th>
              <th className="py-2 pr-4">Odometer</th>
              <th className="py-2 pr-4">Anomaly</th>
              <th className="py-2 pr-4">Verified</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {logs.map((f) => (
              <tr
                key={f.id}
                className="cursor-pointer border-b border-border/60 hover:bg-muted/20"
                onClick={() => setSelected(f)}
              >
                <td className="py-2 pr-4 text-xs">{new Date(f.logged_at).toLocaleString()}</td>
                <td className="py-2 pr-4 text-xs">{assetLabel(f.asset_id)}</td>
                <td className="py-2 pr-4 tabular-nums">{f.liters.toFixed(1)}</td>
                <td className="py-2 pr-4 tabular-nums">
                  {(f.cost_minor / 100).toFixed(2)} {f.currency}
                </td>
                <td className="py-2 pr-4 tabular-nums">{f.odometer?.toLocaleString() ?? '—'}</td>
                <td className="py-2 pr-4">
                  {f.is_anomaly ? (
                    <span title={f.anomaly_reason ?? ''} className="text-amber-300">
                      ⚠ anomaly
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="py-2 pr-4">
                  {f.verified_at ? (
                    <span className="text-emerald-300">✓</span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="py-2 text-right text-xs">
                  <button className="text-primary hover:underline">View</button>
                </td>
              </tr>
            ))}
            {!loading && logs.length === 0 && (
              <tr>
                <td colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                  No fuel logs match this filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <FuelLogDetailModal
          log={selected}
          assetLabel={assetLabel(selected.asset_id)}
          onClose={() => setSelected(null)}
          onVerified={(updated) => {
            setLogs((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
            setSelected(updated);
          }}
        />
      )}
    </div>
  );
}

function FilterBtn({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={
        'px-3 py-1.5 text-sm transition-colors ' +
        (active
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground')
      }
    >
      {children}
    </button>
  );
}
